import { useMemo } from "react";
import DOMPurify from "dompurify";
import { marked } from "marked";

// Defense-in-depth: force noopener/noreferrer on all links that open new tabs.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.hasAttribute("href")) {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

/**
 * Renders a Markdown string as sanitized HTML.
 *
 * Uses `marked` for Markdown→HTML conversion, then DOMPurify to strip any
 * dangerous constructs (script tags, event handlers, javascript: URLs, etc.).
 * This is the ONLY approved way to render AI-generated markdown inside the app —
 * do NOT use `dangerouslySetInnerHTML` directly elsewhere.
 *
 * Props:
 *   - text: raw markdown string (may come from the LLM, so untrusted)
 *   - className: optional wrapper class (Tailwind prose recommended)
 *   - testId: optional data-testid override (default: "safe-markdown")
 */
export default function SafeMarkdown({ text, className = "", testId = "safe-markdown" }) {
  const html = useMemo(() => {
    if (!text || typeof text !== "string") return "";
    const raw = marked.parse(text, { async: false, breaks: true, gfm: true });
    return DOMPurify.sanitize(raw, {
      // Deny anything with side-effects; allow common formatting tags only.
      ALLOWED_TAGS: [
        "h1", "h2", "h3", "h4", "h5", "h6",
        "p", "br", "hr", "strong", "em", "b", "i", "u", "s",
        "ul", "ol", "li",
        "blockquote", "code", "pre",
        "a", "span",
        "table", "thead", "tbody", "tr", "th", "td",
      ],
      ALLOWED_ATTR: ["href", "title", "target", "rel", "class"],
      ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|#)/i,
      ADD_ATTR: ["target"],
    });
  }, [text]);

  return (
    <div
      className={`safe-markdown ${className}`}
      data-testid={testId}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
